import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Stack,
  LinearProgress,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SpeedIcon from "@mui/icons-material/Speed";
import { Bar } from "react-chartjs-2";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import NoEntryFilter from "../Common/NoEntryFilter";
import { fetchExitBlockingConfidenceAudit } from "../../services/exitBlockingApi";

const cameraBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) => {
          const val = context.parsed.y;
          return `Avg Confidence: ${(val * 100).toFixed(1)}% (${val.toFixed(3)})`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: { color: "gray" },
      grid: { display: false },
    },
    y: {
      ticks: {
        color: "gray",
        callback: (val) => `${Math.round(val * 100)}%`,
      },
      min: 0,
      max: 1,
      grid: { color: "rgba(200, 200, 200, 0.15)" },
    },
  },
};

const histogramBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) => `Event Count: ${context.parsed.y}`,
      },
    },
  },
  scales: {
    x: {
      ticks: { color: "gray" },
      grid: { display: false },
    },
    y: {
      ticks: { color: "gray", precision: 0 },
      beginAtZero: true,
      grid: { color: "rgba(200, 200, 200, 0.15)" },
    },
  },
};

const ExitBlockingConfidenceAudit = () => {
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 8;

  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  const loadData = useCallback(async (activeFilter) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (activeFilter.camera_id) params.camera_id = activeFilter.camera_id;
      if (activeFilter.start_date) params.start_date = activeFilter.start_date;
      if (activeFilter.end_date) params.end_date = activeFilter.end_date;
      if (activeFilter.start_time) params.start_time = activeFilter.start_time;
      if (activeFilter.end_time) params.end_time = activeFilter.end_time;
      if (activeFilter.location) params.location = activeFilter.location;
      if (activeFilter.area) params.area = activeFilter.area;
      if (activeFilter.building) params.building = activeFilter.building;
      if (activeFilter.floor_level) params.floor_level = activeFilter.floor_level;
      if (activeFilter.zone) params.zone = activeFilter.zone;

      const data = await fetchExitBlockingConfidenceAudit(params, token);
      setAuditData(data || {});
    } catch (err) {
      console.error("Error fetching exit-blocking confidence audit:", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.detail ||
          "Unable to load model confidence audit data."
      );
      setAuditData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadData(filter);
    }
  }, [filter, loadData, token]);

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Filter Exit-Blocking Confidence Audit",
      content: (
        <NoEntryFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={(newFilter) => {
            setFilter(newFilter);
            setPage(1);
          }}
        />
      ),
      sendReq: () => {
        childRef.current?.submit();
      },
    });
  };

  const summary = auditData?.summary || {};
  const perCamera = Array.isArray(auditData?.per_camera)
    ? auditData.per_camera
    : Array.isArray(auditData?.average_per_camera)
    ? auditData.average_per_camera
    : [];

  const histogram = Array.isArray(auditData?.histogram)
    ? auditData.histogram
    : Array.isArray(auditData?.distribution)
    ? auditData.distribution
    : [];

  const perEvent = Array.isArray(auditData?.per_event)
    ? auditData.per_event
    : Array.isArray(auditData?.events)
    ? auditData.events
    : [];

  const totalEvents =
    summary.total_events ??
    summary.total_detections ??
    perEvent.length ??
    0;

  const rawMeanConf =
    summary.avg_confidence ??
    summary.mean_confidence ??
    (perCamera.length > 0
      ? perCamera.reduce((acc, c) => acc + (c.avg_confidence || c.confidence || 0), 0) /
        perCamera.length
      : 0);

  const meanConfidence = rawMeanConf > 1 ? rawMeanConf / 100 : rawMeanConf;

  const highConfCount =
    summary.high_confidence_count ??
    perEvent.filter((e) => (e.confidence > 1 ? e.confidence / 100 : e.confidence) >= 0.8)
      .length;

  const lowConfCount =
    summary.low_confidence_count ??
    perEvent.filter((e) => (e.confidence > 1 ? e.confidence / 100 : e.confidence) < 0.6)
      .length;

  const cameraLabels = perCamera.map(
    (c) => c.camera_name 
  );
  const cameraConfValues = perCamera.map((c) => {
    const raw = c.avg_confidence ?? c.confidence ?? 0;
    return raw > 1 ? raw / 100 : raw;
  });

  const cameraChartData = {
    labels: cameraLabels.length ? cameraLabels : ["No Data"],
    datasets: [
      {
        label: "Avg Confidence Score",
        data: cameraLabels.length ? cameraConfValues : [0],
        backgroundColor: "rgba(255, 152, 0, 0.75)",
        borderColor: "rgb(255, 152, 0)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const histLabels = histogram.map(
    (h) => h.bucket || h.range || h.bin || `${h.min ?? ""}-${h.max ?? ""}`
  );
  const histCounts = histogram.map((h) => h.count ?? h.total ?? 0);

  const histogramChartData = {
    labels: histLabels.length ? histLabels : ["No Data"],
    datasets: [
      {
        label: "Obstruction Detections",
        data: histLabels.length ? histCounts : [0],
        backgroundColor: "rgba(233, 30, 99, 0.75)",
        borderColor: "rgb(233, 30, 99)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const totalPages = Math.ceil(perEvent.length / itemsPerPage) || 1;
  const paginatedEvents = perEvent.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const formatConfidence = (val) => {
    if (val === undefined || val === null) return "—";
    const num = typeof val === "number" ? val : parseFloat(val);
    const normalized = num > 1 ? num : num * 100;
    return `${normalized.toFixed(1)}%`;
  };

  const getConfChipColor = (val) => {
    if (val === undefined || val === null) return "default";
    const num = typeof val === "number" ? val : parseFloat(val);
    const normalized = num > 1 ? num / 100 : num;
    if (normalized >= 0.8) return "success";
    if (normalized >= 0.6) return "info";
    return "warning";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ p: 3, bgcolor: "background.paper" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Model Confidence Audit — Exit Blocking
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Confidence score evaluation and distribution analysis for exit blockage and obstacle detections.
            </Typography>
          </Box>
          <DesBtn text="Filter" handle={openFilter}>
            <FilterAltOutlinedIcon />
          </DesBtn>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "background.default",
                }}
              >
                <SpeedIcon color="warning" sx={{ fontSize: 36 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Mean Confidence
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {(meanConfidence * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "background.default",
                }}
              >
                <AssessmentOutlinedIcon color="info" sx={{ fontSize: 36 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Audited Events
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {totalEvents}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "background.default",
                }}
              >
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 36 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    High Conf (≥80%)
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {highConfCount}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "background.default",
                }}
              >
                <WarningAmberIcon color="error" sx={{ fontSize: 36 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Lower Conf (&lt;60%)
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {lowConfCount}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 2.5, bgcolor: "background.paper", height: "100%" }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Average Confidence per Camera
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Average model detection confidence score by pathway camera stream.
            </Typography>

            {loading ? (
              <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
            ) : perCamera.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{ p: 4, textAlign: "center", bgcolor: "background.default" }}
              >
                <Typography variant="body2" color="text.secondary">
                  No camera-level confidence data available.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ height: 320 }}>
                <Bar data={cameraChartData} options={cameraBarOptions} />
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 2.5, bgcolor: "background.paper", height: "100%" }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Confidence Score Distribution
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Frequency histogram of model confidence buckets across exit obstacle detections.
            </Typography>

            {loading ? (
              <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
            ) : histogram.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{ p: 4, textAlign: "center", bgcolor: "background.default" }}
              >
                <Typography variant="body2" color="text.secondary">
                  No confidence histogram data available.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ height: 320 }}>
                <Bar data={histogramChartData} options={histogramBarOptions} />
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ p: 2.5, bgcolor: "background.paper" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            Event-Level Confidence Audit Log
          </Typography>
          {perEvent.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Showing {(page - 1) * itemsPerPage + 1}–
              {Math.min(page * itemsPerPage, perEvent.length)} of {perEvent.length} events
            </Typography>
          )}
        </Box>

        {loading ? (
          <LinearProgress sx={{ my: 2 }} />
        ) : perEvent.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{ p: 4, textAlign: "center", bgcolor: "background.default" }}
          >
            <Typography variant="body2" color="text.secondary">
              No event-level confidence records found matching criteria.
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Event ID</strong></TableCell>
                    <TableCell><strong>Camera</strong></TableCell>
                    <TableCell><strong>Location / Zone</strong></TableCell>
                    <TableCell><strong>Timestamp</strong></TableCell>
                    <TableCell><strong>Confidence Score</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedEvents.map((ev, idx) => {
                    const id = ev.event_id || ev.incident_id || ev.id || `ev-${idx}`;
                    return (
                      <TableRow key={id} hover>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {String(id).slice(0, 10)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {ev.camera_name }
                        </TableCell>
                        <TableCell>
                          {ev.zone_name || ev.zone || ev.location || "—"}
                          {ev.building ? ` (${ev.building})` : ""}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {ev.timestamp || ev.event_timestamp || ev.entered_at
                              ? new Date(
                                  ev.timestamp || ev.event_timestamp || ev.entered_at
                                ).toLocaleString()
                              : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatConfidence(ev.confidence)}
                            color={getConfChipColor(ev.confidence)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ev.status || "detected"}
                            size="small"
                            sx={{ textTransform: "capitalize" }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  size="small"
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Card>
    </Box>
  );
};

export default ExitBlockingConfidenceAudit;
